import React, { useState } from "react";
import { useNotificationsCenter } from "../../contexts/NotificationsContext";
import { useRelativeTime } from "../../utils/relativeTime";
import { UserNotification } from "../../types/api";
import { NotificationMessage } from "./NotificationMessage";
import { NotificationEventIcon } from "./NotificationEventIcon";

const BellIcon: React.FC<{ color?: string }> = ({ color = "#374151" }) => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
);

interface RowProps {
    notification: UserNotification;
    onOpen: (patientId: number) => void;
    onMarkRead: (id: number) => void;
}

const NotificationRow: React.FC<RowProps> = ({ notification, onOpen, onMarkRead }) => {
    const relative = useRelativeTime(notification.notification_datetime);
    const isRead = notification.is_read;

    return (
        <div
            onClick={() => {
                // Clicking anywhere on the row (except the patient name) marks it read.
                if (!isRead) onMarkRead(notification.id);
            }}
            style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "10px 12px",
                borderBottom: "1px solid #eef2f2",
                cursor: "pointer",
                backgroundColor: isRead ? "#f7f9f9" : "#ffffff",
            }}
        >
            <div style={{ flexShrink: 0, marginTop: 2 }}>
                <NotificationEventIcon event={notification.notification_event} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        fontSize: 13,
                        lineHeight: 1.4,
                        textAlign: "right",
                        color: isRead ? "#9ca3af" : "#1f2937",
                        fontWeight: isRead ? 400 : 500,
                    }}
                >
                    <NotificationMessage
                        message={notification.message}
                        patientName={notification.patient_name}
                        onOpenPatient={() => {
                            // Clicking the name marks the row read AND opens the patient page.
                            if (!isRead) onMarkRead(notification.id);
                            onOpen(notification.patient_id);
                        }}
                    />
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", textAlign: "right", marginTop: 2 }}>
                    {relative}
                </div>
            </div>
        </div>
    );
};

export const NotificationBell: React.FC = () => {
    const { notifications, unreadCount, refresh, markAsRead, removeAll, openPatient } =
        useNotificationsCenter();
    const [open, setOpen] = useState(false);

    const handleToggle = () => {
        setOpen((o) => {
            const next = !o;
            // Refetch from backend each time the user opens the center.
            if (next) refresh();
            return next;
        });
    };

    const handleOpenPatient = (patientId: number) => {
        setOpen(false);
        openPatient(patientId);
    };

    return (
        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <div
                onClick={handleToggle}
                style={{ position: "relative", cursor: "pointer", display: "flex", alignItems: "center" }}
                title="התראות"
            >
                <BellIcon />
                {unreadCount > 0 && (
                    <span
                        style={{
                            position: "absolute",
                            top: -6,
                            right: -8,
                            backgroundColor: "#EF4444",
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 700,
                            minWidth: 18,
                            height: 18,
                            borderRadius: 9,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 4px",
                            lineHeight: 1,
                        }}
                    >
                        {unreadCount}
                    </span>
                )}
            </div>

            {open && (
                <>
                    {/* Backdrop to close on outside click */}
                    <div
                        onClick={() => setOpen(false)}
                        style={{ position: "fixed", inset: 0, zIndex: 1500 }}
                    />
                    <div
                        dir="rtl"
                        style={{
                            position: "absolute",
                            top: 34,
                            right: 0,
                            width: 360,
                            maxHeight: 440,
                            backgroundColor: "#fff",
                            borderRadius: 8,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                            border: "1px solid #B4D9D4",
                            zIndex: 1600,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                        }}
                    >
                        {/* Header */}
                        <div
                            style={{
                                backgroundColor: "#3b5d6b",
                                color: "#fff",
                                padding: "8px 12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <span style={{ fontSize: 14, fontWeight: 600 }}>התראות</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <button
                                    onClick={removeAll}
                                    title="נקה את כל ההתראות"
                                    aria-label="נקה את כל ההתראות"
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        color: "#fff",
                                        cursor: "pointer",
                                        lineHeight: 0,
                                        padding: 2,
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#fff"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        <line x1="10" y1="11" x2="10" y2="17" />
                                        <line x1="14" y1="11" x2="14" y2="17" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setOpen(false)}
                                    title="סגור"
                                    aria-label="סגור"
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        color: "#fff",
                                        cursor: "pointer",
                                        fontSize: 16,
                                        lineHeight: 1,
                                        padding: 2,
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        {/* List */}
                        <div style={{ overflowY: "auto" }}>
                            {notifications.length === 0 ? (
                                <div
                                    style={{
                                        padding: "20px 12px",
                                        textAlign: "center",
                                        color: "#9ca3af",
                                        fontSize: 13,
                                    }}
                                >
                                    אין התראות
                                </div>
                            ) : (
                                notifications.map((n) => (
                                    <NotificationRow
                                        key={n.id}
                                        notification={n}
                                        onOpen={handleOpenPatient}
                                        onMarkRead={markAsRead}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
